'use client';

import React, { useState, useMemo } from 'react';
import { Box, TextField, Fade, Button, Grid, Paper } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';

const RowCalculator = ({ onComplete }) => {
  const { formData, setFormData } = useEstimator();
  const [overrideCost, setOverrideCost] = useState('');
  const [rowData, setRowData] = useState(() =>
    formData?.rowCalculator?.length
      ? formData.rowCalculator
      : Array.from({ length: 6 }, (_, i) => ({
          county: i === 0 ? 'All' : '',
          footage: '',
          costPerFoot: '',
          percentOfRoute: '0.00%',
          totalCost: '-',
        }))
  );

  const handleGridEdit = (event) => {
    const updated = [...rowData];
    const index = event.node.rowIndex;
    const updatedRow = { ...event.data };

    const footage = parseFloat(updatedRow.footage);
    const costPerFoot = parseFloat(updatedRow.costPerFoot);
    const totalRouteFootage = Number(formData.quantities[5]?.quantity) || 0;

    if (!isNaN(footage) && !isNaN(costPerFoot)) {
      updatedRow.totalCost = (footage * costPerFoot).toFixed(2);
    } else {
      updatedRow.totalCost = '-';
    }

    if (!isNaN(footage) && totalRouteFootage > 0) {
      const percent = (footage / totalRouteFootage) * 100;
      updatedRow.percentOfRoute = `${percent.toFixed(2)}%`;
    } else {
      updatedRow.percentOfRoute = '0.00%';
    }

    updated[index] = updatedRow;
    setRowData(updated);
  };

  const handleAddRow = () => {
    setRowData((prev) => [
      ...prev,
      {
        county: '',
        footage: '',
        costPerFoot: '',
        percentOfRoute: '0.00%',
        totalCost: '-',
      },
    ]);
  };

  const handleNext = () => {
    const averageCostPerFoot =
      overrideCost && !isNaN(Number(overrideCost))
        ? Number(overrideCost)
        : totalFootage > 0
          ? totalCost / totalFootage
          : 0;

    const averageCostPerRod = averageCostPerFoot * 16.5;

    setFormData((prev) => ({
      ...prev,
      rowCalculator: rowData,
      overrideCost,
      averageROWCostPerFoot: averageCostPerFoot.toFixed(2),
      averageROWCostPerRod: averageCostPerRod.toFixed(2),
      totalPercentOfRoute: totalPercentOfRoute.toFixed(2),
    }));

    onComplete();
  };

  const columnDefs = useMemo(
    () => [
      { headerName: 'Counties', field: 'county', editable: true, flex: 1 },
      { headerName: 'ft per county', field: 'footage', editable: true, flex: 1 },
      { headerName: '$ / ft', field: 'costPerFoot', editable: true, flex: 1 },
      {
        headerName: '% of Route',
        field: 'percentOfRoute',
        flex: 1,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      {
        headerName: 'Total Cost',
        field: 'totalCost',
        flex: 1,
        valueFormatter: ({ value }) => (value !== '-' ? `$${value}` : '-'),
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
    ],
    []
  );

  const totalCost = rowData.reduce((sum, row) => {
    const cost = parseFloat(row.totalCost);
    return sum + (isNaN(cost) ? 0 : cost);
  }, 0);

  const totalFootage = rowData.reduce((sum, row) => {
    const footage = parseFloat(row.footage);
    return sum + (isNaN(footage) ? 0 : footage);
  }, 0);

  const totalPercentOfRoute = rowData.reduce((sum, row) => {
    const percent = parseFloat(row.percentOfRoute.replace('%', ''));
    return sum + (isNaN(percent) ? 0 : percent);
  }, 0);

  return (
    <Fade in>
      <Box display="flex" flexDirection="column" gap={2} mt={4}>
        {/* Header Fields - Styled */}
        <Grid container spacing={2}>
          {/* Cost Inputs */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#F9F9F9' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="ROW Cost $/ft Override"
                    value={overrideCost}
                    onChange={(e) => setOverrideCost(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff3e0',
                        boxShadow: '0px 1px 2px rgba(0,0,0,0.08)',
                      },
                      '& .MuiInputLabel-root': {
                        color: '#d32f2f',
                        fontWeight: 'bold',
                      },
                      '& .MuiInputBase-input': {
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Average ROW Cost $/ft"
                    value={`$${(overrideCost && !isNaN(Number(overrideCost))
                      ? Number(overrideCost)
                      : totalFootage > 0
                        ? totalCost / totalFootage
                        : 0
                    ).toFixed(2)}`}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        boxShadow: '0px 1px 2px rgba(0,0,0,0.04)',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Average ROW Cost $/rod"
                    value={`$${(
                      (overrideCost && !isNaN(Number(overrideCost))
                        ? Number(overrideCost)
                        : totalFootage > 0
                          ? totalCost / totalFootage
                          : 0) * 16.5
                    ).toFixed(2)}`}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        boxShadow: '0px 1px 2px rgba(0,0,0,0.04)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Route Stats */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#F9F9F9' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Total Route Footage"
                    value={formData.quantities[5].quantity}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        boxShadow: '0px 1px 2px rgba(0,0,0,0.04)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="%"
                    value={`${totalPercentOfRoute.toFixed(2)}%`}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        boxShadow: '0px 1px 2px rgba(0,0,0,0.04)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* AG Grid Display */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor: '#F9F9F9',
            borderRadius: 2,
            borderLeft: '4px solid #223A64',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true }}
              onCellValueChanged={handleGridEdit}
              domLayout="autoHeight"
            />
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={handleAddRow}>
            Add Row
          </Button>
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default RowCalculator;
